import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseKyc} from './constants';
import {
  IKycAdditionalDocumentRequest,
  IKycAdditionalDocumentResponse,
  IPostPlaidTokenRequest,
  IPostPlaidTokenResponse,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);

      await setWafHeaders(baseUrl, headers);

      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseKyc.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const kycApi = createApi({
  reducerPath: EReducersPath.KYC_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    postTokenPlaid: build.mutation<
      IPostPlaidTokenResponse,
      IPostPlaidTokenRequest
    >({
      query: body => ({
        url: apiBaseKyc.endpoints.postTokenPlaid,
        method: HttpMethod.POST,
        body,
      }),
    }),
    postTokenPlaidIdv: build.mutation<IPostPlaidTokenResponse, void>({
      query: () => ({
        url: apiBaseKyc.endpoints.postTokenIdvPlaid,
        method: HttpMethod.POST,
      }),
    }),
    postDocsSubmitted: build.mutation<void, void>({
      query: () => ({
        url: apiBaseKyc.endpoints.postDocsSubmitted,
        method: HttpMethod.POST,
      }),
    }),
    putAdditionalDocuments: build.mutation<
      IKycAdditionalDocumentResponse,
      IKycAdditionalDocumentRequest
    >({
      query: body => ({
        url: apiBaseKyc.endpoints.putUploadAdditionalDocument,
        method: HttpMethod.PUT,
        body: body,
      }),
    }),
  }),
});

export const {
  usePostTokenPlaidMutation,
  usePostTokenPlaidIdvMutation,
  usePostDocsSubmittedMutation,
  usePutAdditionalDocumentsMutation,
} = kycApi;
